import type { PropsWithChildren } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import {
  DndZoneContextProvider,
  useDraggingIds,
} from '@frameio/assets/operations';
import { useAssetOperationCheck } from '@frameio/assets/shared';
import {
  type GetSharesForProjectNavigationQuery,
  ShareSettings,
  type ShareSettingsFragment,
} from '@frameio/graphql-client/generated';
import {
  ContextMenuContextProvider,
  useActiveProjectId,
} from '@frameio/shared/contexts';
import { useSetSelectedSharePanelIdContext } from '@frameio/shared/contexts/AllSharesListContext/SelectedSharePanelIdContext';
import type { UseGlobalDndMonitorArgs } from '@frameio/shared/dnd';
import { useGlobalDndMonitor } from '@frameio/shared/dnd';
import { getLink } from '@frameio/shared/routing';
import { ListLinkWithPrefetch } from '@frameio/shared/routing/components/ListLinkWithPrefetch';
import { useTrack } from '@frameio/shared/segment/hooks';
import { ShareActionsContextProvider } from '@frameio/shared/shares/contexts/ShareActionsContext';
import { useUpdateShareName } from '@frameio/shared/shares/share-builder/hooks';
import {
  useAddAssetsToShare,
  useShareActionsMenu,
  UseShareActionsMenuItems,
} from '@frameio/shares/hooks';
import { trackingMarksStorage } from '@frameio/utils/storage/appUtilsStorage';
import { Box, List, ListItem, RouterProvider } from '@frameio/vapor';
import {
  type ProjectNavItemType,
  useProjectNavigationActivation,
} from '../ProjectNavActivationManager';
import { AllSharesNavItem } from './AllSharesNavItem';
import type { ShareDndNamespace } from './ShareNavItem';
import { ShareNavItem } from './ShareNavItem';

type SharesArgs = {
  dndNamespace: ShareDndNamespace;
  shares?:
    | NonNullable<
        NonNullable<GetSharesForProjectNavigationQuery['project']>['shares']
      >['nodes']
    | null;
};

const ALL_SHARES_ID = 'all-shares-item';

function SharesComponent({ dndNamespace, shares: sharesProp }: SharesArgs) {
  const projectId = useActiveProjectId();
  const { activate } = useProjectNavigationActivation();
  const setActivePanelId = useSetSelectedSharePanelIdContext();

  // inline editing hooks
  const [editingId, setEditingId] = useState<string | null>(null);
  const updateShareName = useUpdateShareName();
  const { checkShareRisks } = useAssetOperationCheck();
  const shares = useMemo(() => sharesProp ?? [], [sharesProp]);

  const { shareActionsDeleteDialog, getShareActionsMenuContent } =
    useShareActionsMenu({
      actionSource: 'ProjectNavContextMenu',
      menuItems: {
        [UseShareActionsMenuItems.COPY_LINK]: true,
        [UseShareActionsMenuItems.DELETE]: true,
        [UseShareActionsMenuItems.DUPLICATE]: true,
        [UseShareActionsMenuItems.RENAME_SHARE]: true,
        [UseShareActionsMenuItems.OPEN_IN_NEW_TAB]: true,
        [UseShareActionsMenuItems.TOGGLE_ACCESS]: true,
      },
      onSelectRenameShare: (id?: string) => setEditingId(id || null),
    });
  const allItems = useMemo(
    () => [
      { id: ALL_SHARES_ID, editing: false },
      ...shares.map((share) => ({
        id: share.id,
        editing: editingId === share.id,
      })),
    ],
    [editingId, shares]
  );
  const handleActivation = useCallback(
    (id: string, type: ProjectNavItemType) => {
      activate(id, type, () => {
        trackingMarksStorage.set(
          'share-builder-viewed-source',
          'ProjectNavSharesList',
          'session'
        );
      });
    },
    [activate]
  );

  const renderItem = useCallback(
    ({ id, editing }: { id: string; editing: boolean }) => {
      if (id === ALL_SHARES_ID) {
        return (
          <ListItem
            key={id}
            href={getLink({ page: 'allShares', projectId })}
            marginX={SHARE_ITEM_MARGIN_X}
            onAction={() => {
              // Always land on standard Share Links, not the last-used filter
              setActivePanelId('all-shares');
              handleActivation('all-shares', 'all-shares');
            }}
          >
            <AllSharesNavItem />
          </ListItem>
        );
      }
      return (
        <ListItem
          key={id}
          href={getLink({ page: 'shareBuilder', shareId: id, projectId })}
          marginX={SHARE_ITEM_MARGIN_X}
          onAction={() => handleActivation(id, 'share')}
        >
          <ShareNavItem
            dndNamespace={dndNamespace}
            editing={editing}
            menuContent={getShareActionsMenuContent(id)}
            onRename={(name) => {
              updateShareName({ name, shareId: id });
              setEditingId(null);
            }}
            shareId={id}
          />
        </ListItem>
      );
    },
    [
      dndNamespace,
      getShareActionsMenuContent,
      handleActivation,
      projectId,
      setActivePanelId,
      updateShareName,
    ]
  );

  return (
    <DndZoneContextProvider zone="share-nav">
      <ShareActionsContextProvider checkShareRisks={checkShareRisks}>
        <ContextMenuContextProvider>
          <HandleSharesDragEnd dndNamespace={dndNamespace} />
          <StyledBox>
            <ListLinkProvider>
              <List
                animate={true}
                aria-label="Share Links"
                itemGap={4}
                items={allItems}
                paddingBottom={20}
                variant="unstyled"
              >
                {renderItem}
              </List>
            </ListLinkProvider>
          </StyledBox>
        </ContextMenuContextProvider>
        {shareActionsDeleteDialog}
      </ShareActionsContextProvider>
    </DndZoneContextProvider>
  );
}

export const Shares = memo(SharesComponent);

const SHARE_ITEM_MARGIN_X = 12;

function ListLinkProvider(props: PropsWithChildren) {
  const router = useRouter();
  return (
    <RouterProvider
      LinkComponent={ListLinkWithPrefetch as unknown as React.ComponentType}
      navigate={router.push}
      {...props}
    />
  );
}

function HandleSharesDragEnd({
  dndNamespace,
}: {
  dndNamespace: ShareDndNamespace;
}) {
  const { cache } = useApolloClient();

  const { draggingIds } = useDraggingIds();
  const { addAssetsToShare } = useAddAssetsToShare();

  const track = useTrack();

  const handleDragEnd = useCallback<
    NonNullable<UseGlobalDndMonitorArgs['onDragEnd']>
  >(
    ({ over }) => {
      if (!over || !over.id) return;
      if (over.data?.current?.namespace === dndNamespace) {
        addAssetsToShare({
          assetIds: Array.from(draggingIds),
          shareId: over.id as string,
        });

        const shareFragment = cache.readFragment<ShareSettingsFragment>({
          fragment: ShareSettings,
          fragmentName: 'ShareSettings',
          id: cache.identify({ __typename: 'Share', id: over.id }),
          returnPartialData: true,
        });

        track('share-assets-added', {
          creator_user_id: shareFragment?.creator?.id,
          num_items_added: draggingIds.size,
          share_id: over.id as string,
          source: 'DragAndDrop',
        });
      }
    },
    [addAssetsToShare, cache, dndNamespace, draggingIds, track]
  );

  useGlobalDndMonitor({
    onDragEnd: handleDragEnd,
  });

  return null;
}

const StyledBox = styled(Box)`
  display: grid;
  height: 100%;
`;
